import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  motion, AnimatePresence, useReducedMotion, useSpring, useTransform,
} from "framer-motion";
import {
  ArrowRight, Play, Sparkles, TrendingUp, Star, Users, Calendar, PenTool,
  MessageSquare, CheckCircle2, Circle, Loader2, Rocket, Bell, HelpCircle,
  Menu, X, Globe, Linkedin, ArrowUpRight, ShieldCheck, FileText,
} from "lucide-react";
import { Logo } from "../components/Logo";
import { ShowcaseMode } from "../components/ShowcaseMode";
import { useLang, LANGS } from "../lib/i18n";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";

const features = [
  { icon: Sparkles, title: "AI Command Center", desc: "Posez une question, recevez un plan d'action prêt à lancer." },
  { icon: PenTool, title: "Content Studio", desc: "Posts, promos, emails et scripts générés en un clic." },
  { icon: Calendar, title: "Smart Calendar", desc: "Un planning marketing mensuel proposé par l'IA." },
  { icon: Star, title: "Reputation Center", desc: "Collectez plus d'avis et répondez plus vite." },
  { icon: Users, title: "Loyalty Center", desc: "Réactivez vos clients dormants automatiquement." },
  { icon: TrendingUp, title: "Growth Simulator", desc: "Projetez l'impact de vos décisions en temps réel." },
];

/* ---------- Magnetic wrapper (desktop pointer only) ---------- */
function Magnetic({ children }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [t, setT] = useState({ x: 0, y: 0 });
  const onMove = (e) => {
    if (reduce || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setT({ x: (e.clientX - (r.left + r.width / 2)) * 0.25, y: (e.clientY - (r.top + r.height / 2)) * 0.4 });
  };
  return (
    <div ref={ref} onMouseMove={onMove} onMouseLeave={() => setT({ x: 0, y: 0 })}
      className="magnetic inline-flex" style={{ transform: `translate(${t.x}px, ${t.y}px)` }}>
      {children}
    </div>
  );
}

/* ---------- Narrative Business Score ring ---------- */
function HeroRing({ value }) {
  const size = 132, stroke = 11, r = (size - stroke) / 2, c = 2 * Math.PI * r;
  const mv = useSpring(0, { stiffness: 70, damping: 20 });
  const num = useTransform(mv, (v) => Math.round(v));
  const offset = useTransform(mv, (v) => c - (v / 100) * c);
  useEffect(() => { mv.set(value); }, [value, mv]);
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="heroRing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00E5FF" /><stop offset="100%" stopColor="#6366F1" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} fill="none" />
        <motion.circle cx={size / 2} cy={size / 2} r={r} stroke="url(#heroRing)" strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={c} style={{ strokeDashoffset: offset, filter: "drop-shadow(0 0 6px rgba(99,102,241,0.6))" }} />
      </svg>
      <div className="absolute flex flex-col items-center">
        <motion.span className="font-display font-black text-4xl leading-none">{num}</motion.span>
        <span className="text-xs sm:text-[10px] text-[hsl(var(--muted-foreground))] mt-0.5">/ 100</span>
      </div>
    </div>
  );
}

/* ---------- The living "Augmented Merchant" scene ---------- */
function HeroScene() {
  const reduce = useReducedMotion();
  const [phase, setPhase] = useState(reduce ? 5 : 0);
  useEffect(() => {
    if (reduce) return;
    let i = 0;
    const id = setInterval(() => { i = (i + 1) % 6; setPhase(i); }, 1700);
    return () => clearInterval(id);
  }, [reduce]);

  const score = phase === 0 ? 0 : phase >= 5 ? 77 : 74;
  const missionState = phase >= 5 ? "done" : phase === 4 ? "progress" : "todo";
  const statusText = phase === 0 ? "Analyse en cours…" : "6 opportunités détectées";

  return (
    <div className="relative w-full h-[300px] sm:h-[460px] lg:h-[540px]" data-testid="hero-scene">
      {/* merchant ambiance — premium photoreal shop scene */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <img src="/hero-merchant.jpg" alt="" aria-hidden="true"
          className="w-[92%] h-[92%] object-cover rounded-2xl opacity-95 drop-shadow-[0_20px_60px_rgba(79,70,229,0.35)]" />
      </div>

      {/* data-flow lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true">
        <line x1="30%" y1="30%" x2="70%" y2="22%" stroke="#00E5FF" strokeWidth="1.5" className="flow-line" opacity="0.5" />
        <line x1="35%" y1="55%" x2="18%" y2="72%" stroke="#6366F1" strokeWidth="1.5" className="flow-line" opacity="0.45" />
        <line x1="60%" y1="60%" x2="80%" y2="74%" stroke="#00E5FF" strokeWidth="1.5" className="flow-line" opacity="0.4" />
      </svg>

      {/* Business Score panel */}
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}
        className="panel drift absolute top-4 left-0 sm:left-2 p-4 w-[220px]" data-testid="hero-score-demo">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-[hsl(var(--muted-foreground))]">Business Score</span>
          <AnimatePresence>
            {phase >= 5 && (
              <motion.span key="delta" initial={{ opacity: 0, scale: 0.6 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">+3</motion.span>
            )}
          </AnimatePresence>
        </div>
        <div className="flex justify-center"><HeroRing value={score} /></div>
        <div className="grid grid-cols-3 gap-1 mt-3">
          {[["Visibilité", 78], ["Réputation", 82], ["Présence", 85]].map(([l, v]) => (
            <div key={l} className="text-center">
              <div className="text-sm font-display font-bold neon-text">{v}</div>
              <div className="text-[9px] text-[hsl(var(--muted-foreground))]">{l}</div>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Status chip (analyse → opportunités) */}
      <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
        className="panel absolute top-6 right-0 sm:right-2 px-3 py-2 flex items-center gap-2">
        {phase === 0 ? <Loader2 size={14} className="animate-spin text-neon" /> : <Sparkles size={14} className="text-neon" />}
        <AnimatePresence mode="wait">
          <motion.span key={statusText} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className="text-xs font-medium whitespace-nowrap">{statusText}</motion.span>
        </AnimatePresence>
      </motion.div>

      {/* Recommendation card */}
      <AnimatePresence>
        {phase >= 2 && (
          <motion.div key="reco" initial={{ opacity: 0, y: 18, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
            className="panel drift2 absolute right-0 sm:right-2 top-[42%] p-3.5 w-[230px]">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-7 h-7 rounded-lg bg-primary/20 flex items-center justify-center text-neon"><Rocket size={14} /></div>
              <span className="text-xs font-semibold">Recommandation IA</span>
            </div>
            <p className="text-xs text-[hsl(var(--muted-foreground))] leading-snug">Lancer l'offre « Mardi Découverte » -20% pour remplir vos heures creuses.</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mission card À FAIRE → EN COURS → TERMINÉE */}
      <AnimatePresence>
        {phase >= 3 && (
          <motion.div key="mission" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
            className="panel absolute left-0 sm:left-2 bottom-4 p-3.5 w-[240px]">
            <div className="flex items-center gap-2">
              {missionState === "done" ? <CheckCircle2 size={16} className="text-emerald-400" />
                : missionState === "progress" ? <Loader2 size={16} className="text-neon animate-spin" />
                : <Circle size={16} className="text-[hsl(var(--muted-foreground))]" />}
              <span className={`text-xs font-medium ${missionState === "done" ? "line-through text-[hsl(var(--muted-foreground))]" : ""}`}>Créer l'offre du mardi soir</span>
            </div>
            <div className="mt-2 flex items-center gap-1.5">
              {["todo", "progress", "done"].map((s, i) => (
                <div key={s} className="flex-1 h-1 rounded-full overflow-hidden bg-white/8">
                  <div className="h-full bg-gradient-to-r from-primary to-neon transition-all duration-500"
                    style={{ width: (["todo", "progress", "done"].indexOf(missionState) >= i) ? "100%" : "0%" }} />
                </div>
              ))}
            </div>
            <p className="mt-1.5 text-[10px] uppercase tracking-wide font-semibold" style={{ color: missionState === "done" ? "#10B981" : missionState === "progress" ? "#00E5FF" : "#94A3B8" }}>
              {missionState === "done" ? "Terminée" : missionState === "progress" ? "En cours" : "À faire"}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Notification pill */}
      <AnimatePresence>
        {phase >= 5 && (
          <motion.div key="notif" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }}
            className="panel absolute right-2 bottom-6 px-3 py-2 flex items-center gap-2">
            <Bell size={13} className="text-emerald-400" />
            <span className="text-[11px]">Business Score +3</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------- Module mini-demos ---------- */
function CommandDemo() {
  return (
    <div className="space-y-1.5">
      <div className="ml-auto w-max max-w-[85%] text-[10px] px-2.5 py-1.5 rounded-xl rounded-br-sm bg-primary text-white">Plus de clients le mardi ?</div>
      <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.4 }}
        className="w-max max-w-[90%] text-[10px] px-2.5 py-1.5 rounded-xl rounded-bl-sm glass border border-white/10 flex items-center gap-1">
        <Sparkles size={11} className="text-neon" /> Voici un plan en 4 missions…
      </motion.div>
    </div>
  );
}
function ContentDemo() {
  return (
    <div className="space-y-2 pt-1">
      <div className="gen-line w-[90%]" /><div className="gen-line w-full" /><div className="gen-line w-[70%]" />
      <div className="flex gap-1 pt-0.5">{["#café", "#Lyon", "#promo"].map((h) => <span key={h} className="text-xs sm:text-[9px] text-neon">{h}</span>)}</div>
    </div>
  );
}
function CalendarDemo() {
  return (
    <div className="grid grid-cols-7 gap-1">
      {Array.from({ length: 14 }).map((_, i) => (
        <div key={i} className="aspect-square rounded-[4px] bg-white/[0.04] relative">
          {i === 5 && <motion.div initial={{ scale: 0 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ delay: 0.3, type: "spring" }} className="absolute inset-0.5 rounded-[3px] bg-gradient-to-br from-primary to-neon" />}
          {i === 9 && <div className="absolute inset-0.5 rounded-[3px] bg-neon/40" />}
        </div>
      ))}
    </div>
  );
}
function CountUp({ to, decimals = 0, className = "" }) {
  const mv = useSpring(0, { stiffness: 60, damping: 18 });
  const txt = useTransform(mv, (v) => v.toFixed(decimals).replace(".", ","));
  return <motion.span className={className} onViewportEnter={() => mv.set(to)}>{txt}</motion.span>;
}
function ReputationDemo() {
  return (
    <div className="flex items-center gap-2">
      <span className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} size={13} className="text-yellow-400" fill="currentColor" />)}</span>
      <CountUp to={4.6} decimals={1} className="font-display font-black text-xl" />
      <span className="text-[10px] text-emerald-400 flex items-center gap-0.5"><TrendingUp size={11} /> +0,3</span>
    </div>
  );
}
function LoyaltyDemo() {
  return (
    <div className="flex items-end gap-3">
      <div><CountUp to={812} className="font-display font-black text-xl text-neon" /><p className="text-[9px] text-[hsl(var(--muted-foreground))]">actifs</p></div>
      <div><CountUp to={186} className="font-display font-black text-xl text-orange-400" /><p className="text-[9px] text-[hsl(var(--muted-foreground))]">à réactiver</p></div>
    </div>
  );
}
function GrowthDemo() {
  return (
    <svg viewBox="0 0 120 40" className="w-full h-10">
      <motion.polyline fill="none" stroke="#00E5FF" strokeWidth="2" strokeLinecap="round"
        points="0,34 24,28 48,30 72,18 96,12 120,4"
        initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} viewport={{ once: true }} transition={{ duration: 1.1 }}
        style={{ filter: "drop-shadow(0 0 4px rgba(0,229,255,0.6))" }} />
    </svg>
  );
}
const demos = [CommandDemo, ContentDemo, CalendarDemo, ReputationDemo, LoyaltyDemo, GrowthDemo];

export default function Landing() {
  const nav = useNavigate();
  const { t, lang, setLang } = useLang();
  const { startDemo } = useApp();
  const cur = LANGS.find((l) => l.code === lang);
  const goDemo = () => { nav("/demo"); };
  const [showcase, setShowcase] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);

  useEffect(() => {
    const onScroll = () => setShowStickyCta(window.scrollY > 520);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="relative min-h-screen overflow-x-hidden grain">
      {/* Living background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute inset-0 aurora" />
        <div className="absolute inset-0 tech-grid opacity-60" />
        <div className="hero-glow-a drift absolute -top-32 -left-24 w-[520px] h-[520px] rounded-full blur-[40px] opacity-40" />
        <div className="hero-glow-b drift2 absolute top-1/3 -right-24 w-[460px] h-[460px] rounded-full blur-[40px] opacity-40" />
        {[...Array(7)].map((_, i) => (
          <span key={i} className="particle" style={{ left: `${12 + i * 12}%`, bottom: `${8 + (i % 3) * 10}%`, animationDelay: `${i * 0.9}s` }} />
        ))}
      </div>

      {/* Nav */}
      <header className="z-30 glass border-b border-white/5 fixed top-0 inset-x-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-5 h-16 flex items-center justify-between gap-2">
          <div className="min-w-0 shrink overflow-hidden"><Logo /></div>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            {/* Bouton présentation projet — visible mobile (hors menu) */}
            <a href="https://localboostai.tiiny.site/" target="_blank" rel="noopener noreferrer"
              data-testid="discover-project-mobile" title={t("discover_sub")} aria-label={t("discover_project_cta")}
              className="sm:hidden inline-flex items-center gap-1 rounded-full border border-neon/50 bg-neon/10 px-3 min-h-[40px] text-xs font-semibold text-white whitespace-nowrap">
              <Sparkles size={13} className="text-neon" /> {t("discover_short")} <ArrowUpRight size={13} className="text-neon" />
            </a>
            {/* Desktop / tablet controls — unchanged, hidden on mobile */}
            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <a href="https://localboostai.tiiny.site/" target="_blank" rel="noopener noreferrer"
                data-testid="discover-project" title={t("discover_sub")}
                className="group inline-flex items-center gap-2 rounded-full border border-neon/50 bg-neon/10 hover:bg-neon/20 hover:border-neon px-3 lg:px-4 h-10 text-xs lg:text-sm font-semibold text-white whitespace-nowrap transition-colors shadow-[0_4px_20px_-6px_rgba(34,211,238,0.6)]">
                <Sparkles size={14} className="text-neon" />
                <span>{t("discover_project_cta")}</span>
                <ArrowUpRight size={14} className="text-neon transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="landing-lang" className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] hover:text-white px-1 sm:px-2 py-1 transition-colors shrink-0">{cur?.flag} <span className="hidden sm:inline">{cur?.code.toUpperCase()}</span></button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="glass border-white/10">
                  {LANGS.map((l) => <DropdownMenuItem key={l.code} data-testid={`landing-lang-${l.code}`} onClick={() => setLang(l.code)} className="gap-2 cursor-pointer">{l.flag} {l.label}</DropdownMenuItem>)}
                </DropdownMenuContent>
              </DropdownMenu>
              <button data-testid="nav-pricing" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} className="hidden sm:inline text-xs sm:text-sm text-white/80 hover:text-white px-1.5 sm:px-3 py-2 transition-colors shrink-0 whitespace-nowrap">{t("nav_pricing")}</button>
              <button data-testid="landing-login" onClick={() => nav("/login")} className="text-xs sm:text-sm text-white/80 hover:text-white px-1.5 sm:px-3 py-2 transition-colors shrink-0 whitespace-nowrap">{t("login")}</button>
              <Button data-testid="landing-signup" onClick={() => nav("/register")} className="rounded-full bg-primary hover:bg-primary/90 lb-glow-btn shrink-0 whitespace-nowrap text-xs sm:text-sm px-2.5 sm:px-4 h-9 sm:h-10">{t("signup")}</Button>
            </div>
            {/* Mobile hamburger */}
            <button data-testid="landing-menu-btn" onClick={() => setMenuOpen(true)} aria-label={t("menu")}
              className="sm:hidden inline-flex items-center justify-center w-11 h-11 -mr-1 rounded-lg text-white/90 hover:bg-white/10 active:bg-white/15 transition-colors">
              <Menu size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu (mobile only) */}
      <AnimatePresence>
        {menuOpen && (
          <div className="sm:hidden fixed inset-0 z-50" data-testid="landing-mobile-menu" onClick={() => setMenuOpen(false)}>
            <motion.div className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              data-testid="landing-menu-overlay" />
            <motion.div
              initial={{ y: -16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -16, opacity: 0 }}
              transition={{ type: "spring", damping: 26, stiffness: 260 }}
              onClick={(e) => e.stopPropagation()}
              className="absolute top-0 inset-x-0 glass border-b border-white/10 px-4 pt-4 pb-6">
              <div className="flex items-center justify-between h-8 mb-4">
                <Logo />
                <button data-testid="landing-menu-close" onClick={() => setMenuOpen(false)} aria-label={t("close")}
                  className="inline-flex items-center justify-center w-11 h-11 -mr-1 rounded-lg text-white/90 hover:bg-white/10 active:bg-white/15 transition-colors">
                  <X size={24} />
                </button>
              </div>
              <div className="flex flex-col gap-1">
                <button data-testid="landing-menu-pricing"
                  onClick={() => { setMenuOpen(false); setTimeout(() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" }), 60); }}
                  className="flex items-center min-h-[48px] px-3 rounded-xl text-base text-white/90 hover:bg-white/5 active:bg-white/10 transition-colors">
                  {t("nav_pricing")}
                </button>
                <button data-testid="landing-menu-login"
                  onClick={() => { setMenuOpen(false); nav("/login"); }}
                  className="flex items-center min-h-[48px] px-3 rounded-xl text-base text-white/90 hover:bg-white/5 active:bg-white/10 transition-colors">
                  {t("login")}
                </button>
                <Button data-testid="landing-menu-signup"
                  onClick={() => { setMenuOpen(false); nav("/register"); }}
                  className="mt-1 w-full justify-center rounded-full min-h-[48px] bg-primary hover:bg-primary/90 lb-glow-btn text-base">
                  {t("signup")}
                </Button>
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="flex items-center gap-2 px-3 mb-2 text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
                    <Globe size={14} /> {LANGS.find((l) => l.code === lang)?.label}
                  </div>
                  <div className="flex gap-2 px-1">
                    {LANGS.map((l) => (
                      <button key={l.code} data-testid={`landing-menu-lang-${l.code}`}
                        onClick={() => { setLang(l.code); }}
                        className={`flex-1 min-h-[44px] rounded-xl text-sm flex items-center justify-center gap-1.5 border transition-colors ${lang === l.code ? "border-primary/40 bg-primary/15 text-white" : "border-white/10 text-white/80 hover:bg-white/5"}`}>
                        <span className="text-base">{l.flag}</span> {l.code.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Hero */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 pt-24 sm:pt-28 lg:pt-36 pb-16 lg:pb-24 grid lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-10 items-center">
        <div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs text-neon mb-6">
            <span className="relative flex h-1.5 w-1.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-neon opacity-60" /><span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-neon" /></span>
            Copilote commercial intelligent
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.08 }}
            className="font-display font-black text-4xl sm:text-5xl lg:text-[4.2rem] leading-[1.03] tracking-tight">
            {(() => { const parts = t("hero_title").split(". "); const first = parts.shift(); const rest = parts.join(". "); return (<>{first}.<br /><span className="neon-text">{rest}</span></>); })()}
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.22 }}
            className="mt-4 sm:mt-6 text-lg text-[hsl(var(--muted-foreground))] max-w-md">{t("hero_sub")}</motion.p>

          {/* pipeline chips */}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="mt-4 sm:mt-6 flex flex-wrap items-center gap-2 text-xs sm:text-[11px]">
            {["Analyse", "Décide", "Agit", "Mesure"].map((s, i) => (
              <React.Fragment key={s}>
                <span className="px-2.5 py-1 rounded-full glass border border-white/10 text-white/80">{s}</span>
                {i < 3 && <ArrowRight size={12} className="text-neon/70" />}
              </React.Fragment>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.36 }}
            className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3">
            <Magnetic>
              <Button data-testid="cta-analyze" onClick={() => nav("/register")} size="lg"
                className="rounded-full h-12 px-7 text-sm font-bold bg-gradient-to-r from-primary to-neon text-white lb-glow-btn w-full sm:w-auto">
                {t("cta_analyze")} <ArrowRight size={18} className="ml-1" />
              </Button>
            </Magnetic>
            <Magnetic>
              <Button data-testid="cta-demo" onClick={goDemo} variant="outline" size="lg"
                className="rounded-full h-12 px-7 text-sm font-bold border-white/15 bg-white/5 hover:bg-white/10 text-white w-full sm:w-auto">
                <Play size={16} className="mr-1" /> {t("cta_demo")}
              </Button>
            </Magnetic>
          </motion.div>

          <motion.button data-testid="cta-showcase" onClick={() => setShowcase(true)}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
            className="mt-5 inline-flex items-center gap-2 text-sm text-[hsl(var(--muted-foreground))] hover:text-white transition-colors group min-h-[44px] py-1">
            <span className="w-8 h-8 rounded-full glass border border-white/10 flex items-center justify-center text-neon group-hover:scale-110 transition-transform"><Play size={13} /></span>
            Voir la démo animée · 15 s
          </motion.button>

          <div className="mt-4">
            <button data-testid="cta-how-it-works" onClick={() => nav("/app/help")}
              className="inline-flex items-center gap-1.5 text-xs text-[hsl(var(--muted-foreground))] hover:text-neon transition-colors min-h-[44px] sm:min-h-0 py-2 sm:py-0">
              <HelpCircle size={13} /> {t("how_it_works")}
            </button>
          </div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.25 }}>
          <HeroScene />
        </motion.div>
      </section>

      {/* PREUVES CLIENTS — bloc très visible, juste après le hero */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 pb-6 sm:pb-10 lg:pb-14" data-testid="proof-section">
        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="relative rounded-3xl border border-neon/40 bg-gradient-to-br from-neon/[0.08] to-white/[0.02] p-4 sm:p-8 overflow-hidden shadow-[0_10px_50px_-12px_rgba(34,211,238,0.35)]">
          <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full bg-neon/20 blur-[60px] pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-neon/50 bg-neon/10 px-3 py-1 mb-2.5 sm:mb-4" data-testid="proof-badge">
              <CheckCircle2 size={14} className="text-neon" />
              <span className="text-xs font-black tracking-[0.15em] text-neon">{t("proof_title")}</span>
            </div>
            <h2 className="font-display font-black text-lg sm:text-2xl leading-tight" data-testid="proof-tagline">{t("proof_tagline")}</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 sm:mt-3 max-w-3xl leading-snug sm:leading-relaxed" data-testid="proof-text">{t("proof_text")}</p>

            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row sm:items-stretch gap-1.5 sm:gap-1" data-testid="proof-flow">
              {[
                { icon: Users, label: t("proof_step1") },
                { icon: MessageSquare, label: t("proof_step2") },
                { icon: Sparkles, label: t("proof_step3") },
                { icon: Rocket, label: t("proof_step4") },
              ].map((s, i, arr) => (
                <React.Fragment key={i}>
                  <div className="flex-1 flex sm:flex-col items-center gap-2 sm:gap-1.5 rounded-xl bg-white/5 border border-white/10 px-3 py-2 sm:py-3 text-left sm:text-center">
                    <s.icon size={18} className="text-neon shrink-0" />
                    <span className="text-[11px] sm:text-xs font-bold tracking-wide leading-tight">{s.label}</span>
                  </div>
                  {i < arr.length - 1 && <div className="flex items-center justify-center text-neon/70 rotate-90 sm:rotate-0 sm:px-0.5"><ArrowRight size={14} /></div>}
                </React.Fragment>
              ))}
            </div>

            <a href="https://localboostai.tiiny.site" target="_blank" rel="noopener noreferrer" data-testid="proof-cta"
              className="group mt-4 sm:mt-6 inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-bold text-sm px-6 h-12 min-h-[48px]">
              {t("proof_cta")} <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>
        </motion.div>
      </section>

      {/* PROTECTION / DÉPÔT INPI e-Soleau — compact, élégant, cohérent */}
      <section className="relative z-10 max-w-5xl mx-auto px-5 pb-6 sm:pb-10" data-testid="inpi-section">
        <motion.div initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="relative rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.06] to-white/[0.02] p-4 sm:p-6 overflow-hidden shadow-[0_8px_40px_-16px_rgba(34,211,238,0.3)]">
          <div className="absolute -top-12 -left-8 w-40 h-40 rounded-full bg-neon/10 blur-[50px] pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
            <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
              <div className="shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl bg-neon/10 border border-neon/30 flex items-center justify-center" data-testid="inpi-icon">
                <ShieldCheck size={22} className="text-neon" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display font-black text-sm sm:text-base tracking-wide leading-tight" data-testid="inpi-title">{t("inpi_title")}</h3>
                <p className="text-xs sm:text-sm text-[hsl(var(--muted-foreground))] mt-1.5 leading-snug sm:leading-relaxed" data-testid="inpi-desc">{t("inpi_desc")}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2.5" data-testid="inpi-meta">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full border border-neon/30 bg-neon/10 text-neon">
                    <FileText size={12} /> {t("inpi_ref")}
                  </span>
                  <span className="text-[11px] text-white/50 font-semibold" data-testid="inpi-date">{t("inpi_date")}</span>
                </div>
                <a href="https://www.inpi.fr/realiser-demarches/propriete-intellectuelle/se-preparer-au-depot-dune-e-soleau" target="_blank" rel="noopener noreferrer" data-testid="inpi-learn"
                  className="inline-flex items-center gap-1 mt-2 py-2 sm:py-0 min-h-[40px] sm:min-h-0 text-[11px] font-semibold text-neon/80 hover:text-neon underline underline-offset-2 transition-colors">
                  {t("inpi_learn")} <ArrowUpRight size={11} />
                </a>
              </div>
            </div>
            <a href="/inpi-esoleau-DSO2026037431.pdf" target="_blank" rel="noopener noreferrer" data-testid="inpi-cta"
              className="group shrink-0 inline-flex items-center justify-center gap-2 w-full sm:w-auto rounded-full border border-neon/40 bg-white/5 hover:bg-neon/10 text-white font-bold text-xs sm:text-sm px-5 h-11 min-h-[44px] transition-colors">
              {t("inpi_cta")} <ArrowUpRight size={16} className="text-neon transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </motion.div>
      </section>

      {/* Positioning / differentiation block */}
      <section className="relative z-10 max-w-4xl mx-auto px-5 pb-6 lg:pb-10" data-testid="positioning-block">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="lb-card p-7 lg:p-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs text-neon mb-5">
            <Sparkles size={13} /> {t("pos_heading")}
          </div>
          <p className="font-display font-bold text-xl md:text-2xl leading-snug mb-6">{t("pos_phrase")}</p>

          <div className="flex flex-wrap items-center gap-2 text-xs mb-6" data-testid="positioning-pipeline">
            {[t("pos_pipe_data"), t("pos_pipe_priorities"), t("pos_pipe_actions")].map((s, i) => (
              <React.Fragment key={i}>
                <span className="px-3 py-1.5 rounded-full glass border border-white/10 text-white/85 font-semibold uppercase tracking-wide">{s}</span>
                {i < 2 && <ArrowRight size={13} className="text-neon/70" />}
              </React.Fragment>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center text-neon shrink-0"><Users size={16} /></div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{t("pos_specialized")}</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center text-neon shrink-0"><TrendingUp size={16} /></div>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{t("pos_experience")}</p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Modules / features */}
      <section className="relative z-10 max-w-7xl mx-auto px-5 pb-28">
        <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="font-display font-bold text-2xl md:text-3xl mb-2">Un vrai copilote, pas un tableau de bord de plus.</motion.h2>
        <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
          className="text-[hsl(var(--muted-foreground))] mb-10 max-w-xl">Analysez votre situation, recevez des actions concrètes et suivez vos progrès.</motion.p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => {
            const Demo = demos[i];
            return (
              <motion.div key={f.title} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }} transition={{ delay: (i % 3) * 0.08, duration: 0.55 }}
                whileHover={{ y: -6 }} className="lb-card tilt p-6" data-testid={`feature-${i}`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center text-neon"><f.icon size={20} /></div>
                  <h3 className="font-display font-bold text-lg">{f.title}</h3>
                </div>
                <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">{f.desc}</p>
                <div className="rounded-xl bg-white/[0.02] border border-white/5 p-3 min-h-[72px] flex items-center">
                  <div className="w-full"><Demo /></div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="mt-14 lb-card p-8 lg:p-12 text-center relative overflow-hidden aurora">
          <h3 className="font-display font-black text-2xl lg:text-4xl mb-3">Prêt à booster votre commerce ?</h3>
          <p className="text-[hsl(var(--muted-foreground))] mb-6">Testez la démo NOVA COFFEE sans inscription.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Magnetic>
              <Button data-testid="cta-demo-2" onClick={goDemo} className="rounded-full h-12 px-7 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn w-full sm:w-auto">{t("cta_demo")}</Button>
            </Magnetic>
            <Magnetic>
              <Button data-testid="cta-analyze-2" onClick={() => nav("/register")} variant="outline" className="rounded-full h-12 px-7 border-white/15 bg-white/5 hover:bg-white/10 text-white w-full sm:w-auto">{t("cta_analyze")}</Button>
            </Magnetic>
          </div>
        </motion.div>
      </section>

      {/* Tarif premium */}
      <section id="pricing" className="relative z-10 max-w-5xl mx-auto px-5 pb-28 scroll-mt-20" data-testid="pricing-section">
        <div className="text-center mb-8">
          <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="font-display font-black text-3xl md:text-4xl leading-tight">{t("pricing_h1")}</h2>
            <p className="text-sm md:text-base text-[hsl(var(--muted-foreground))] mt-3 max-w-xl mx-auto whitespace-pre-line">{t("pricing_sub")}</p>
          </motion.div>
        </div>

        <div className="grid lg:grid-cols-[1fr_0.9fr] gap-6 items-stretch">
          {/* Prix — pièce centrale */}
          <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="beam p-[1.5px] rounded-[1.6rem]">
            <div className="rounded-[1.55rem] bg-[var(--lb-card)] p-7 md:p-9 h-full flex flex-col items-center text-center relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-56 h-56 rounded-full bg-neon/10 blur-3xl pointer-events-none" />
              <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide px-3 py-1 rounded-full border border-neon/30 bg-neon/10 text-neon mb-5" data-testid="pricing-badge"><Sparkles size={12} /> {t("pricing_badge")}</span>
              <div className="flex items-end justify-center gap-2">
                <span className="font-display font-black text-6xl md:text-7xl neon-text leading-none" data-testid="pricing-amount">39,90&nbsp;€</span>
              </div>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{t("pricing_permonth")}</p>
              <Button data-testid="pricing-cta" onClick={() => nav("/register")} className="mt-7 w-full rounded-full min-h-[3.25rem] h-auto py-3 px-4 sm:px-6 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-semibold text-sm sm:text-base leading-tight">
                <span className="whitespace-normal text-center">{t("pricing_cta")}</span> <ArrowRight size={17} className="ml-1.5 shrink-0" />
              </Button>
              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
                <span>{t("pricing_free7")}</span><span className="opacity-40">•</span><span>{t("pricing_nocard")}</span><span className="opacity-40">•</span><span>{t("pricing_nocommit")}</span>
              </p>
              <p className="text-[13px] text-white/80 mt-4 font-medium">{t("pricing_microline")}</p>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-3 italic">{t("pricing_value")}</p>
            </div>
          </motion.div>

          {/* Cycle LOCALBOOST — ce qu'il y a derrière le prix */}
          <div className="lb-card p-4 sm:p-6 md:p-7" data-testid="pricing-cycle">
            <p className="text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3 sm:mb-4">{t("pricing_cycle_title")}</p>
            <div className="space-y-0">
              {["comprendre", "analyser", "decider", "planifier", "agir", "mesurer", "reajuster"].map((k, i, arr) => (
                <motion.div key={k} initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                  className="flex items-start gap-2.5 sm:gap-3" data-testid={`pricing-step-${k}`}>
                  <div className="flex flex-col items-center">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-primary to-neon flex items-center justify-center text-[11px] font-bold text-white">{i + 1}</span>
                    {i < arr.length - 1 && <span className="w-px h-3 sm:h-6 bg-gradient-to-b from-neon/40 to-transparent my-0.5" />}
                  </div>
                  <div className="pb-1 sm:pb-1.5">
                    <p className="text-sm font-semibold leading-tight">{t(`pricing_cy_${k}`)}</p>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] leading-tight">{t(`pricing_cyd_${k}`)}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <footer className="relative z-10 border-t border-white/5 pt-5 pb-24 sm:py-8 px-5" data-testid="legal-footer">
        <div className="max-w-5xl mx-auto text-center">
          <p className="font-display font-black text-sm tracking-wide">LOCALBOOST AI</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{t("footer_creator")}</p>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">© 2026 Philippe BARBIER — {t("footer_rights")}</p>
          <a href="https://www.linkedin.com/in/philippe-barbier-69713858" target="_blank" rel="noopener noreferrer"
            data-testid="footer-linkedin" title={t("footer_linkedin")} aria-label={t("footer_linkedin")}
            className="mt-2 sm:mt-3 inline-flex items-center justify-center w-11 h-11 rounded-full text-[hsl(var(--muted-foreground))] hover:text-white hover:bg-white/5 transition-colors">
            <Linkedin size={20} />
          </a>
          <div className="mt-2 sm:mt-4 text-xs text-[hsl(var(--muted-foreground))] flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 leading-tight">
            <button data-testid="footer-mentions" onClick={() => nav("/legal/mentions")} className="inline-flex items-center justify-center px-2 sm:px-1 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 rounded hover:text-white transition-colors">{t("legal_mentions")}</button>
            <span aria-hidden className="text-white/25">•</span>
            <button data-testid="footer-privacy" onClick={() => nav("/legal/privacy")} className="inline-flex items-center justify-center px-2 sm:px-1 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 rounded hover:text-white transition-colors">{t("legal_privacy")}</button>
            <span aria-hidden className="text-white/25">•</span>
            <button data-testid="footer-terms" onClick={() => nav("/legal/terms")} className="inline-flex items-center justify-center px-2 sm:px-1 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 rounded hover:text-white transition-colors">{t("legal_terms")}</button>
            <span aria-hidden className="text-white/25">•</span>
            <button data-testid="footer-sales" onClick={() => nav("/legal/sales")} className="inline-flex items-center justify-center px-2 sm:px-1 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 rounded hover:text-white transition-colors">{t("legal_sales")}</button>
            <span aria-hidden className="text-white/25">•</span>
            <button data-testid="footer-cookies" onClick={() => nav("/legal/cookies")} className="inline-flex items-center justify-center px-2 sm:px-1 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 rounded hover:text-white transition-colors">{t("legal_cookies")}</button>
          </div>
        </div>
      </footer>

      <ShowcaseMode open={showcase} onClose={() => setShowcase(false)}
        onDemo={() => { setShowcase(false); goDemo(); }}
        onAnalyze={() => { setShowcase(false); nav("/register"); }} />

      {/* Sticky mobile CTA — appears on scroll, mobile only */}
      <AnimatePresence>
        {showStickyCta && !menuOpen && (
          <motion.div
            initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            exit={menuOpen ? { opacity: 0, transition: { duration: 0 } } : { y: 80, opacity: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 260 }}
            className="sm:hidden fixed inset-x-0 bottom-0 z-40 px-3 pt-2"
            style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom))" }}
            data-testid="sticky-cta-bar">
            <div className="glass border border-white/10 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.45)] p-2">
              <Button data-testid="sticky-cta-analyze" onClick={() => nav("/register")}
                className="w-full justify-center rounded-xl min-h-[48px] bg-gradient-to-r from-primary to-neon text-white lb-glow-btn font-bold text-sm">
                {t("cta_analyze")} <ArrowRight size={18} className="ml-1.5" />
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
